import apiClient from './apiClient';

// ============================================================
// Types
// ============================================================

export interface FileEntry {
  path: string;
  lines: number;
}

export interface EndpointEntry {
  method: string;
  path: string;
  description: string;
}

export interface SolidScoreEntry {
  score: number;
  note: string;
}

export type SolidDetail = Record<string, SolidScoreEntry>;

export interface SubmoduleItem {
  id: number;
  formId: string;
  submoduleId: string;
  name: string;
  status: string;
  solidScore: number;
  solidDetail: SolidDetail | null;
  e2eStatus: string;
  files: FileEntry[] | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ModuleListItem {
  formId: string;
  name: string;
  specDir: string;
  auditScore: number;
  specSizeKb: number;
  dependencyLevel: number | null;
  backendStatus: string;
  beSolidScore: number;
  frontendStatus: string;
  feSolidScore: number;
  e2eStatus: string;
  status: string;
  submoduleCount: number;
  updatedAt: string;
}

export interface ModuleDetail {
  formId: string;
  name: string;
  specDir: string;
  auditScore: number;
  dependencyLevel: number | null;
  backendStatus: string;
  beSolidScore: number;
  beSolidDetail: SolidDetail | null;
  frontendStatus: string;
  feSolidScore: number;
  feSolidDetail: SolidDetail | null;
  e2eStatus: string;
  status: string;
  files: FileEntry[] | null;
  endpoints: EndpointEntry[] | null;
  backendCompletedAt: string | null;
  frontendCompletedAt: string | null;
  e2eCompletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  submodules: SubmoduleItem[] | null;
}

export interface TestResult {
  testName: string;
  pass: boolean;
  durationMs: number;
  package?: string;
}

export interface TestRun {
  id: number;
  formId: string;
  submoduleId: string | null;
  category: string;
  format: string;
  totalTests: number;
  passCount: number;
  failCount: number;
  totalDurationMs: number;
  results: TestResult[];
  runAt: string;
}

export interface TrendPoint {
  runAt: string;
  category: string;
  formId: string;
  totalTests: number;
  passCount: number;
  failCount: number;
  totalDurationMs: number;
}

export interface StatsModules {
  total: number;
  done: number;
  wip: number;
  bug: number;
  blocked: number;
  notStarted: number;
  completionRate: number;
}

export interface StatsTestCategory {
  totalTests: number;
  passCount: number;
  failCount: number;
  lastRunAt: string | null;
}

export interface StatsData {
  modules: StatsModules;
  tests: {
    backend: StatsTestCategory;
    e2e: StatsTestCategory;
  };
}

export interface ModuleListResponse {
  data: ModuleListItem[];
  total: number;
  page: number;
  size: number;
}

export interface TestRunListResponse {
  data: TestRun[];
  total: number;
  page: number;
  size: number;
}

export interface FilterState {
  search: string;
  status: string;
  page: number;
  size: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
}

export interface TestRunFilters {
  page: number;
  size: number;
  formId?: string;
  category?: string;
  failOnly?: boolean;
}

export interface TrendFilters {
  formId?: string;
  category?: string;
  limit?: number;
}

// ============================================================
// API methods
// ============================================================

export const devApi = {
  // Stats
  async getStats(): Promise<StatsData> {
    const res = await apiClient.get('/api/dev/stats');
    return res.data.data;
  },

  // Modules
  async getModules(params: {
    page?: number;
    size?: number;
    status?: string;
    search?: string;
    sortBy?: string;
    order?: string;
  }): Promise<ModuleListResponse> {
    const res = await apiClient.get('/api/dev/modules', { params });
    return res.data;
  },

  async getModule(formId: string): Promise<ModuleDetail> {
    const res = await apiClient.get(`/api/dev/modules/${encodeURIComponent(formId)}`);
    return res.data.data;
  },

  async updateModule(formId: string, updates: Record<string, unknown>): Promise<ModuleDetail> {
    const res = await apiClient.put(`/api/dev/modules/${encodeURIComponent(formId)}`, updates);
    return res.data.data;
  },

  // Submodules
  async getSubmodules(formId: string): Promise<SubmoduleItem[]> {
    const res = await apiClient.get(`/api/dev/modules/${encodeURIComponent(formId)}/submodules`);
    return res.data.data;
  },

  // Test Runs
  async getTestRuns(params: {
    page?: number;
    size?: number;
    formId?: string;
    category?: string;
    failOnly?: boolean;
  }): Promise<TestRunListResponse> {
    const res = await apiClient.get('/api/dev/test-runs', { params });
    return res.data;
  },

  // Test Trend
  async getTestTrend(params: {
    formId?: string;
    category?: string;
    limit?: number;
  }): Promise<TrendPoint[]> {
    const res = await apiClient.get('/api/dev/test-runs/trend', { params });
    return res.data.data;
  },

  // Import
  async importModules(modules: Partial<ModuleListItem>[]): Promise<{ imported: number; created: number; updated: number }> {
    const res = await apiClient.post('/api/dev/import/modules', { modules });
    return res.data.data;
  },

  // Agent report push APIs
  async pushBackendReport(data: Record<string, unknown>): Promise<unknown> {
    const res = await apiClient.post('/api/dev/report/backend', data);
    return res.data.data;
  },

  async pushSolidReport(data: Record<string, unknown>): Promise<unknown> {
    const res = await apiClient.post('/api/dev/report/solid', data);
    return res.data.data;
  },

  async pushE2EReport(data: Record<string, unknown>): Promise<unknown> {
    const res = await apiClient.post('/api/dev/report/e2e', data);
    return res.data.data;
  },

  async pushSubmoduleReport(data: Record<string, unknown>): Promise<unknown> {
    const res = await apiClient.post('/api/dev/report/submodule', data);
    return res.data.data;
  },

  async pushFixReport(data: Record<string, unknown>): Promise<unknown> {
    const res = await apiClient.post('/api/dev/report/fix', data);
    return res.data.data;
  },
};
